import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Button,
  Space,
  Modal,
  Upload,
} from 'antd';

import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

import type { Buttons } from "../../../../types/Buttons";

interface ButtonModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void;
  initialValues?: Buttons | null;
  isEditMode?: boolean;
  buttons: Buttons[];
}

const ButtonModal: React.FC<ButtonModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
  buttons,
}) => {
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageFileList, setImageFileList] = useState<UploadFile[]>([]);


  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  };


   // image preview 
    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as File);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
        setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
    };

    // Utility function to convert image to base64
    const getBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });


  const handleSubmit = async (values: any) => {
    try {
      const formData = new FormData();
      formData.append('button_name', values.button_name);

      if (imageFile) {
        formData.append('attachments', imageFile);
      }

      if (initialValues?.button_id) {
        formData.append('button_id', initialValues.button_id.toString());
      }

      const url = isEditMode ? 'edit-button' : 'add-button';
      const method = isEditMode ? 'put' : 'post';

      const data = await apiRequest({
        method,
        url,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onCreate(data);
      showSuccess(isEditMode ? 'Button updated successfully!' : 'Button created successfully!');
      form.resetFields();
      if (!isModalClose) {
        setImageFile(null);
        setImageFileList([]);
      }

      if (isModalClose) {
        onCancel();
      }
    } catch (error) {
      showError(`Failed to ${isEditMode ? 'update' : 'create'} button`);
    }
  };

  useEffect(() => {
    if (visible && isEditMode && initialValues) {
      form.setFieldsValue({
        button_name: initialValues.button_name,
      });
      if (initialValues.button_image) {
        setImageFileList([
          {
            uid: '-1',
            name: 'image.png',
            status: 'done',
            url: initialValues.button_image,
          },
        ]);
        setImageFile(null);
      }
    } else if (visible) {
      form.resetFields();
      setImageFileList([]);
      setImageFile(null);
    }
  }, [visible, isEditMode, initialValues, form]);

  const uploadButton = (
    <div
      style={{
        width: 220,
        height: 220,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <PlusOutlined style={{ fontSize: 24 }} />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <>
      <Modal
        title={isEditMode ? 'Edit Button' : 'Add New Button'}
        visible={visible}
        onCancel={onCancel}
        footer={null}
        width="90%"
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="button_name"
                className="px-8"
                label={
                  <span>
                    Button Name&nbsp;
                    <span style={{ fontSize: '12px', color: '#888' }}>
                      (Max 100 characters)
                    </span>
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter the button name' },
                  { max: 100, message: 'Maximum 100 characters allowed' },
                ]}
              >
                <Input
                  showCount
                  maxLength={100}
                  placeholder="e.g., Casual Button"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Upload Button Image" required>
                <Upload
                  beforeUpload={() => false}
                  listType="picture-card"
                  maxCount={1}
                  onPreview={handlePreview} // 👈 Add this
                  fileList={imageFileList}
                  onChange={({ fileList }) => {
                    setImageFileList(fileList);
                    const rawFile = fileList[0]?.originFileObj;
                    if (rawFile instanceof File) {
                      setImageFile(rawFile);
                    } else {
                      setImageFile(null);
                    }
                  }}
                >
                  {imageFileList.length >= 1 ? null : uploadButton}
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space>
              <Button onClick={onCancel}>Cancel</Button>
              {!isEditMode && (
                <Button
                  type="primary"
                  htmlType="submit"
                  onClick={() => setIsModalClose(false)}
                  disabled={isSubmitDisabled()}
                >
                  Submit & Create New Button
                </Button>
              )}
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setIsModalClose(true)}
                disabled={isSubmitDisabled()}
              >
                Submit
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
          visible={previewOpen}
          title={previewTitle}
          footer={null}
          centered
          onCancel={() => setPreviewOpen(false)}
      >
          <img
              alt="Preview"
              style={{ width: '100%' }}
              src={previewImage}
      />
      </Modal>
    </>

  );
};

export default ButtonModal;
